// ═══════════════════════════════════════════════════
//  LinkedInfoBlock — Main Controller
//  Level 1 → simple single-page view (no tabs)
//  Level 2 → full tabbed interface (4 tabs)
// ═══════════════════════════════════════════════════
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Split tab components
import OverviewTab from "./scan/OverviewTab";
import TagsMapTab from "./scan/TagsMapTab";
import TrackingTab from "./scan/TrackingTab";
import SecurityTab from "./scan/SecurityTab";

// Types & helpers
import { LinkedInfoBlockProps, Store } from "./scan/types";
import {
  API_BASE_URL,
  fmtAddress,
  pickProductImage,
  formatDateTime,
  calculateDistance,
} from "./scan/helpers";

// Icons
import {
  Package,
  MapPin,
  ExternalLink,
  Clock,
  CheckCircle,
  AlertTriangle,
  Share2,
  Shield,
  Truck,
  Eye,
  Navigation,
  Zap,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  BadgeCheck,
  Link2,
} from "lucide-react";

// ═══════════════════════════════════════════════════
//  ⛓ Blockchain Verification Components
// ═══════════════════════════════════════════════════

const BlockchainBadge: React.FC<{ blockchain: any }> = ({ blockchain }) => {
  if (!blockchain || blockchain.level !== "blockchain") return null;
  return (
    <span
      className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold border border-purple-200 flex items-center gap-1"
      title={`Anchored on ${blockchain.chainProof?.network || "blockchain"} at block #${blockchain.chainProof?.blockNumber}`}
    >
      <Link2 className="w-3 h-3" />
      Blockchain Verified
    </span>
  );
};

const BlockchainProofPanel: React.FC<{ blockchain: any }> = ({ blockchain }) => {
  if (!blockchain) return null;
  const { level, message, traceUrl, chainProof } = blockchain;

  if (level === "blockchain" && chainProof) {
    const shortTx = chainProof.transactionHash
      ? `${chainProof.transactionHash.slice(0, 10)}...${chainProof.transactionHash.slice(-8)}`
      : null;
    return (
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-2xl p-5 mt-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-purple-600 text-white rounded-xl p-2.5 flex-shrink-0">
            <Link2 className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-purple-900 flex items-center gap-2 flex-wrap">
              ⛓ Verified on Blockchain
              <span className="bg-purple-200 text-purple-900 px-2 py-0.5 rounded-full text-xs font-semibold">
                {chainProof.network}
              </span>
            </h3>
            <p className="text-xs text-purple-700 mt-1">
              This product's traceability data is permanently anchored on a public blockchain and cannot be tampered with.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          {chainProof.blockNumber !== undefined && chainProof.blockNumber !== null && (
            <div className="bg-white rounded-xl p-3 border border-purple-100">
              <div className="text-[10px] text-purple-500 font-semibold uppercase tracking-wide">Block #</div>
              <div className="text-sm font-bold text-purple-900 mt-0.5">{chainProof.blockNumber}</div>
            </div>
          )}
          {chainProof.anchoredAt && (
            <div className="bg-white rounded-xl p-3 border border-purple-100">
              <div className="text-[10px] text-purple-500 font-semibold uppercase tracking-wide">Anchored</div>
              <div className="text-sm font-bold text-purple-900 mt-0.5">
                {new Date(chainProof.anchoredAt).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>

        {shortTx && (
          <div className="bg-white rounded-xl p-3 border border-purple-100 mb-3">
            <div className="text-[10px] text-purple-500 font-semibold uppercase tracking-wide mb-1">Transaction Hash</div>
            <div className="text-xs font-mono text-purple-900 break-all">{shortTx}</div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {chainProof.explorerUrl && (
            <a
              href={chainProof.explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              View on Explorer
            </a>
          )}
          {traceUrl && (
            <a
              href={traceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-white hover:bg-purple-50 text-purple-700 text-xs font-semibold px-4 py-2 rounded-lg border border-purple-300 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              View Traceability
            </a>
          )}
        </div>
      </div>
    );
  }

  if (level === "pending") {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-5 flex items-center gap-3">
        <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 animate-pulse" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-900">Anchoring on blockchain...</div>
          <div className="text-xs text-amber-700">This usually takes 10-30 seconds. Refresh to update.</div>
        </div>
      </div>
    );
  }

  if (level === "failed") {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mt-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-orange-900">{message}</div>
            {traceUrl && (
              <a
                href={traceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-orange-700 hover:text-orange-900 mt-1"
              >
                <ExternalLink className="w-3 h-3" /> View Traceability
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (level === "url-only" && traceUrl) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <span className="text-sm text-blue-900 font-medium truncate">Traceability available</span>
        </div>
        <a
          href={traceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0"
        >
          <ExternalLink className="w-3 h-3" /> View
        </a>
      </div>
    );
  }

  return null;
};

const LinkedInfoBlock: React.FC<LinkedInfoBlockProps> = ({
  scanData,
  currentLocation,
  store,
  onUpgrade,
  onShare,
}) => {
  const navigate = useNavigate();

  // ═══════════════════════════════════════
  //  STATE
  // ═══════════════════════════════════════
  const [activeTab, setActiveTab] = useState<"overview" | "tagsmap" | "tracking" | "security">("overview");
  const [locationVerification, setLocationVerification] = useState<{
    status: "checking" | "verified" | "failed" | "unknown";
    distance?: number;
    message?: string;
  }>({ status: "unknown" });

  const [stores, setStores] = useState<Store[]>([]);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [mapLocations, setMapLocations] = useState<Array<{ latitude: number; longitude: number; city?: string; tagId: string }>>([]);
  const [isLoadingMapData, setIsLoadingMapData] = useState(false);

  const [shipments, setShipments] = useState<any[]>([]);
  const [isLoadingShipments, setIsLoadingShipments] = useState(false);
  const [, setSelectedFile] = useState<any>(null);

  const [batchCertificates, setBatchCertificates] = useState<any[]>([]);
  const [batchInspections, setBatchInspections] = useState<any[]>([]);
  const [batchDetails, setBatchDetails] = useState<any>(null);
  const [loadingQuality, setLoadingQuality] = useState(false);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // ═══════════════════════════════════════
  //  USE EFFECTS — Data Fetching
  // ═══════════════════════════════════════

  // GPS verification
  useEffect(() => {
    if (store?.latitude && store?.longitude && currentLocation?.latitude && currentLocation?.longitude) {
      setLocationVerification({ status: "checking" });
      const distance = calculateDistance(currentLocation.latitude, currentLocation.longitude, store.latitude, store.longitude);
      const radiusKm = (store.gpsValidation?.radius || 100) / 1000;
      if (distance <= radiusKm) {
        setLocationVerification({ status: "verified", distance: distance * 1000, message: `Location verified within ${Math.round(distance * 1000)}m of store` });
      } else {
        setLocationVerification({ status: "failed", distance: distance * 1000, message: `Location verification failed - ${Math.round(distance * 1000)}m from store (max: ${store.gpsValidation?.radius || 100}m)` });
      }
    }
  }, [store, currentLocation]);

  // Fetch stores by BusinessID
  useEffect(() => {
    const fetchStoresByBusiness = async () => {
      const businessId = scanData?.business?._id || scanData?.product?.business?._id || scanData?.business?.businessId || scanData?.product?.businessId || scanData?.business?.id;
      if (!businessId) return;
      console.log("🛰️ Fetching stores for businessId:", businessId);
      setIsLoadingStores(true);
      try {
        const cleanBase = API_BASE_URL.replace(/\/+$/, "");
        const res = await fetch(`${cleanBase}/stores?businessId=${businessId}`);
        const data = await res.json();
        const storeList = data?.stores || data?.data?.stores || (Array.isArray(data?.data) ? data.data : null);
        if (data?.success && Array.isArray(storeList)) {
          const validStores = storeList
            .map((s: any) => ({ ...s, latitude: parseFloat(s.latitude), longitude: parseFloat(s.longitude) }))
            .filter((s: any) => !isNaN(s.latitude) && !isNaN(s.longitude));
          setStores(validStores);
        }
      } catch (err) { console.error("❌ Error fetching stores:", err); }
      finally { setIsLoadingStores(false); }
    };
    if (scanData) fetchStoresByBusiness();
  }, [scanData]);

  // Fetch traceability
  useEffect(() => {
    const loadTraceability = async () => {
      const batchId = scanData?.traceability?.batchId || scanData?.product?.batchId;
      if (!batchId) return;
      try {
        const cleanBase = API_BASE_URL.replace(/\/+$/, "");
        const res = await fetch(`${cleanBase}/supply-chain/trace/${batchId}`);
        const data = await res.json();
        if (data?.success && data.traceability && scanData) {
          scanData.traceability = data.traceability;
        }
      } catch (err) { console.error("❌ Error loading traceability:", err); }
    };
    if (scanData) loadTraceability();
  }, [scanData]);

  // Fetch map locations
  useEffect(() => {
    const fetchMapLocations = async () => {
      setIsLoadingMapData(true);
      try {
        const cleanBase = API_BASE_URL.replace(/\/+$/, "");
        const productId = scanData?.product?._id || scanData?.product?.productId;
        const tagId = scanData?.tagId;
        let mapData: any[] = [];

        if (productId && productId !== tagId) {
          // Product map — GET /map/product/:productId/countries
          console.log("🗺️ Fetching product map for productId:", productId);
          const response = await fetch(`${cleanBase}/scanConsumer/map/product/${productId}/countries`);
          if (response.ok) {
            const data = await response.json();
            if (data?.success && Array.isArray(data.countries)) {
              mapData = data.countries
                .filter((c: any) => c.lat && c.lng)
                .map((c: any) => ({ lat: c.lat, lng: c.lng, city: c.country || "Unknown", tagId: tagId || productId }));
            }
          } else {
            console.warn(`⚠️ Product map returned ${response.status} — skipping`);
          }
        } else if (tagId) {
          // Tag map — GET /map/tag/:tagId (CORRECT endpoint!)
          console.log("🗺️ Fetching tag map for tagId:", tagId);
          const response = await fetch(`${cleanBase}/scanConsumer/map/tag/${tagId}`);
          if (response.ok) {
            const data = await response.json();
            const raw = data?.mapData || data?.scans || [];
            if (data?.success && Array.isArray(raw)) {
              mapData = raw;
            }
          } else {
            console.warn(`⚠️ Tag map returned ${response.status} — skipping`);
          }
        }

        const validPoints = mapData
          .filter((item: any) => (item.lat || item.latitude) && (item.lng || item.longitude))
          .map((item: any) => ({
            latitude: parseFloat(item.lat || item.latitude),
            longitude: parseFloat(item.lng || item.longitude),
            city: item.city || item.country || "Unknown",
            tagId: item.tagId || tagId || "",
          }));

        setMapLocations(validPoints);
        console.log(`✅ Map loaded: ${validPoints.length} points`);
      } catch (error) {
        console.warn("⚠️ Map load failed — non-critical:", error);
      } finally {
        setIsLoadingMapData(false);
      }
    };
    if (scanData) fetchMapLocations();
  }, [scanData]);

  // Fetch shipments
  useEffect(() => {
    const fetchShipments = async () => {
      const batchId = scanData?.traceability?.batchId || scanData?.product?.batchId;
      if (!batchId) return;
      setIsLoadingShipments(true);
      try {
        const cleanBase = API_BASE_URL.replace(/\/+$/, "");
        const res = await fetch(`${cleanBase}/shipments/batch/${batchId}`);
        const data = await res.json();
        if (data?.success && Array.isArray(data.data?.shipments)) {
          setShipments(data.data.shipments);
        }
      } catch (err) { console.error("❌ Error fetching shipments:", err); }
      finally { setIsLoadingShipments(false); }
    };
    if (scanData) fetchShipments();
  }, [scanData]);

  // Fetch batch details, certificates & inspections
  useEffect(() => {
    const fetchQualityDocs = async () => {
      const batchId = scanData?.traceability?.batchId || scanData?.product?.batchId;
      if (!batchId) return;
      setLoadingQuality(true);

      // ── Try to extract everything from the scan response first ──
      const scanBatch = scanData?.batch || (scanData?.product as any)?.batchDetails;
      if (scanBatch && Object.keys(scanBatch).length > 1) {
        setBatchDetails(scanBatch);
        console.log("✅ Batch details from scan response:", scanBatch);

        // Certificates & inspections are INSIDE the batch document
        const certs = scanBatch.certificates || scanBatch.qualityControl?.certificates || [];
        const insps = scanBatch.inspections || scanBatch.qualityControl?.inspectionReports || [];
        if (certs.length > 0) setBatchCertificates(certs);
        if (insps.length > 0) setBatchInspections(insps);
        console.log(`📋 Certs: ${certs.length}, Inspections: ${insps.length}`);
      }

      // ── Fallback: try API endpoints if scan response didn't include batch ──
      try {
        const cleanBase = API_BASE_URL.replace(/\/+$/, "");

        if (!scanBatch || Object.keys(scanBatch).length <= 1) {
          const batchUrls = [
            `${cleanBase}/scanConsumer/batch/${batchId}/public`,
            `${cleanBase}/batch/${batchId}/public`,
            `${cleanBase}/batch/${batchId}`,
            `${cleanBase}/batches/${batchId}`,
          ];
          for (const url of batchUrls) {
            try {
              const r = await fetch(url);
              if (r.ok) {
                const json = await r.json();
                if (json?.success) {
                  const bd = json.data?.batch || json.data || json.batch;
                  setBatchDetails(bd);
                  // Also extract certs/inspections from fetched batch
                  const certs = bd?.certificates || bd?.qualityControl?.certificates || [];
                  const insps = bd?.inspections || bd?.qualityControl?.inspectionReports || [];
                  if (certs.length > 0) setBatchCertificates(certs);
                  if (insps.length > 0) setBatchInspections(insps);
                  console.log("✅ Batch details from API:", url);
                  break;
                }
              }
            } catch { /* try next URL */ }
          }
        }
      } catch (err) { console.error("❌ Error fetching quality docs:", err); }
      finally { setLoadingQuality(false); }
    };
    if (scanData) fetchQualityDocs();
  }, [scanData]);

  // ═══════════════════════════════════════
  //  HANDLERS
  // ═══════════════════════════════════════

  const handleGenerateCertificate = () => {
    if (!scanData) { alert("No scan data available"); return; }

    let businessName = scanData.business?.companyName || scanData.basicInfo?.companyName || scanData.product?.brand || scanData.product?.business?.companyName || "Unknown Company";
    let batchNumber = scanData.product?.batchId || scanData.traceability?.batchId || (scanData.product as any)?.batch || "N/A";
    const businessId = scanData.business?.businessId || scanData.product?.businessId || scanData.business?.id || scanData.product?.business?.businessId || "unknown";

    const certificateData = {
      certificate: { title: "JepTags Digital Certificate", issueDate: new Date().toISOString(), certificateId: `CERT-${scanData.tagId}-${Date.now().toString().slice(-6)}` },
      product: {
        name: scanData.product?.name || scanData.basicInfo?.productName || "Verified Product",
        brand: scanData.product?.brand || businessName,
        batch: batchNumber,
        warranty: scanData.product?.warranty || "Not specified",
        category: scanData.product?.category || "General",
        image: pickProductImage(scanData.product) || "",
        sku: scanData.product?.sku,
        productId: scanData.product?.productId,
      },
      verification: { tagId: scanData.tagId, scanId: scanData.scanId, status: scanData.verified ? "AUTHENTIC" : "NOT VERIFIED", verificationCode: scanData.authenticity?.verificationCode || "N/A", level: scanData.level, timestamp: scanData.timestamp, confidence: scanData.authenticity?.confidence },
      consumer: { name: scanData.consumer?.name || "Anonymous User", email: scanData.consumer?.email || "N/A", id: scanData.consumer?.id, loyaltyLevel: scanData.consumer?.loyaltyLevel, pointsEarned: scanData.consumer?.pointsEarned },
      business: scanData.business ? { businessId, companyName: businessName, tradingName: scanData.business.tradingName, industry: scanData.business.industry, website: scanData.business.website, email: scanData.business.email, phone: scanData.business.phone, logoUrl: scanData.business.logoUrl || scanData.business.logo, address: typeof scanData.business.address === "string" ? { formatted: scanData.business.address } : scanData.business.address, verification: scanData.business.verification } : null,
      traceability: scanData.traceability ? { batchId: scanData.traceability.batchId, status: scanData.traceability.status, storeId: scanData.traceability.storeId, activation: scanData.traceability.activation, stages: scanData.traceability.stages?.map((s: any) => ({ name: s.name, location: s.location, timestamp: s.timestamp, status: s.status })), totalStages: scanData.traceability.stages?.length } : null,
      store: store ? { storeId: store.storeId, storeName: store.storeName, manager: store.manager, address: fmtAddress(store.address), coordinates: store.latitude && store.longitude ? { latitude: store.latitude, longitude: store.longitude } : null } : null,
      statistics: { totalScans: scanData.stats?.totalScans, uniqueConsumers: scanData.stats?.uniqueConsumers, popularityRank: scanData.stats?.popularityRank, trendingScore: scanData.stats?.trendingScore, geoStats: scanData.stats?.geoStats },
      scanTime: formatDateTime(scanData.timestamp),
      generatedAt: new Date().toISOString(),
    };

    try {
      navigate(`/consumer/certificate/${scanData.tagId}`, { state: certificateData });
      return;
    } catch { /* fallback below */ }

    try {
      const certificateWindow = window.open("", "_blank", "width=1200,height=800,scrollbars=yes");
      if (certificateWindow) {
        certificateWindow.document.write(`<!DOCTYPE html><html><head><title>JepTags Certificate - ${certificateData.certificate.certificateId}</title><script src="https://cdn.tailwindcss.com"></script></head><body class="bg-gray-50"><div class="max-w-6xl mx-auto bg-white shadow-2xl border-2 border-blue-200 rounded-xl overflow-hidden m-4"><div class="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8"><div class="flex items-center justify-between"><div><h1 class="text-3xl font-bold">JepTags Digital Certificate</h1><p class="text-blue-100">Product Authenticity Verification</p></div><div class="text-right"><div class="flex items-center gap-2 text-green-300 mb-2"><span class="text-xl">✅</span><span class="font-semibold">VERIFIED AUTHENTIC</span></div><p class="text-sm text-blue-200">Certificate ID: ${certificateData.certificate.certificateId}</p></div></div></div><div class="p-8"><div class="grid grid-cols-2 gap-8"><div class="bg-gray-50 rounded-lg p-6"><h2 class="text-2xl font-bold mb-4">Product Information</h2><div class="space-y-3"><div><label class="text-sm font-medium text-gray-500">Product Name</label><p class="text-lg font-semibold">${certificateData.product.name}</p></div><div><label class="text-sm font-medium text-gray-500">Brand</label><p class="text-lg font-semibold">${certificateData.product.brand}</p></div><div><label class="text-sm font-medium text-gray-500">Tag ID</label><p class="text-lg font-mono">${certificateData.verification.tagId}</p></div><div><label class="text-sm font-medium text-gray-500">Date</label><p class="text-lg font-semibold">${certificateData.scanTime}</p></div></div></div><div class="text-center"><img src="${certificateData.product.image}" alt="Product" class="w-full h-64 object-cover rounded-lg border-2 mb-4"/><span class="text-6xl">✅</span><p class="text-2xl font-bold text-green-600">AUTHENTIC</p></div></div><div class="flex justify-center gap-4 mt-8"><button onclick="window.print()" class="bg-blue-600 text-white px-6 py-2 rounded-lg">Print</button><button onclick="window.close()" class="bg-gray-500 text-white px-6 py-2 rounded-lg">Close</button></div></div><div class="bg-gray-100 border-t px-8 py-6 text-center text-sm text-gray-600"><p>Generated: ${new Date().toLocaleString()}</p><p>Powered by JepTags</p></div></div></body></html>`);
        certificateWindow.document.close();
        return;
      }
    } catch { /* fallback below */ }

    // Final fallback: download JSON
    const dataStr = JSON.stringify(certificateData, null, 2);
    const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const link = document.createElement("a");
    link.setAttribute("href", dataUri);
    link.setAttribute("download", `jeptags-certificate-${scanData.tagId}-${Date.now()}.json`);
    link.click();
  };

  // ═══════════════════════════════════════
  //  INLINE SUB-COMPONENTS
  // ═══════════════════════════════════════

  const AuthenticityBadge: React.FC = () => {
    if (!scanData) return null;
    const isLevel2 = scanData.level === 2;
    const isVerified = scanData.verified;
    const isRateLimited = scanData.authenticity?.status === "rate_limited";
    const confidence = scanData.authenticity?.confidence || (isVerified ? "high" : "low");
    const verificationCode = scanData.authenticity?.verificationCode || "N/A";

    if (isRateLimited) {
      return (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 p-6 rounded-xl mb-8 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center"><Clock className="w-6 h-6 text-yellow-600" /></div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-yellow-900 mb-1">SCAN COOLDOWN ACTIVE</h3>
              <p className="text-yellow-700 mb-2">This tag was recently scanned. Please wait a moment before scanning again.</p>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full">Status: Rate Limited</span>
                <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full">Tag ID: {scanData.tagId}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return isVerified ? (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-6 rounded-xl mb-8 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center"><CheckCircle className="w-6 h-6 text-green-600" /></div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-green-900 mb-1">AUTHENTIC PRODUCT</h3>
            <p className="text-green-700 mb-2">This product has been verified as authentic. {isLevel2 ? `Verification confidence: ${confidence}` : "Basic verification passed."}</p>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              {verificationCode !== "N/A" && verificationCode !== "RATE_LIMITED" && (
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-mono">Code: {verificationCode}</span>
              )}
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">Status: Activated & Verified</span>
              <button onClick={handleGenerateCertificate} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full hover:bg-purple-200 transition-colors flex items-center gap-1 cursor-pointer">
                <ExternalLink className="w-3 h-3" /> Generate Certificate
              </button>
            </div>
          </div>
          {locationVerification.status === "verified" && (
            <div className="text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-1"><Navigation className="w-5 h-5 text-blue-600" /></div>
              <span className="text-xs text-blue-700 font-medium">GPS Verified</span>
            </div>
          )}
        </div>
      </div>
    ) : (
      <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 p-6 rounded-xl mb-8 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center"><AlertTriangle className="w-6 h-6 text-red-600" /></div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-red-900 mb-1">PRODUCT NOT AUTHENTIC</h3>
            <p className="text-red-700 mb-2">This tag could not be verified as authentic.</p>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full">Status: Not Activated</span>
              <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full">Tag ID: {scanData.tagId}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ProductHero: React.FC = () => {
    if (!scanData) return null;
    const isLevel2 = scanData.level === 2;
    const product = scanData.product;

    const allImages: { url: string; alt: string }[] = [];
    if (product?.images?.length) product.images.forEach((img) => { if (img.url) allImages.push({ url: img.url, alt: img.alt || product.name || "Product" }); });
    if (product?.primaryImage && !allImages.find((i) => i.url === product.primaryImage)) allImages.unshift({ url: product.primaryImage, alt: product.name || "Product" });
    if (product?.imageUrl && !allImages.find((i) => i.url === product.imageUrl)) allImages.push({ url: product.imageUrl, alt: product.name || "Product" });

    const productName = product?.name || scanData.basicInfo?.productName || `Tag ${scanData.tagId}`;
    const productDescription = product?.description || (isLevel2 ? undefined : "Basic verification completed — upgrade to Level 2 for full details");
    const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % (allImages.length || 1));
    const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + (allImages.length || 1)) % (allImages.length || 1));

    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-8">
        <div className="flex flex-col lg:flex-row">
          {/* Image Carousel */}
          <div className="relative lg:w-[420px] flex-shrink-0 bg-gray-50">
            {allImages.length > 0 ? (
              <div className="relative aspect-square lg:aspect-auto lg:h-full">
                <img src={allImages[currentImageIndex]?.url} alt={allImages[currentImageIndex]?.alt} className="w-full h-full object-cover" style={{ minHeight: "320px" }} onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                {allImages.length > 1 && (
                  <>
                    <button onClick={prevImage} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur p-2 rounded-full shadow-md hover:bg-white transition-all"><ChevronLeft className="w-5 h-5 text-gray-700" /></button>
                    <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur p-2 rounded-full shadow-md hover:bg-white transition-all"><ChevronRight className="w-5 h-5 text-gray-700" /></button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {allImages.map((_, idx) => (<button key={idx} onClick={() => setCurrentImageIndex(idx)} className={`w-2.5 h-2.5 rounded-full transition-all ${idx === currentImageIndex ? "bg-teal-600 scale-125" : "bg-white/70"}`} />))}
                    </div>
                    <div className="absolute top-4 right-4 bg-black/50 text-white text-xs px-2.5 py-1 rounded-full backdrop-blur">{currentImageIndex + 1} / {allImages.length}</div>
                  </>
                )}
                {allImages.length > 1 && allImages.length <= 8 && (
                  <div className="absolute bottom-12 left-0 right-0 flex justify-center gap-2 px-4">
                    {allImages.map((img, idx) => (
                      <button key={idx} onClick={() => setCurrentImageIndex(idx)} className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${idx === currentImageIndex ? "border-teal-500 shadow-lg scale-110" : "border-white/60 opacity-70 hover:opacity-100"}`}>
                        <img src={img.url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-50" style={{ minHeight: "320px" }}>
                <div className="text-center"><ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-2" /><p className="text-gray-400 text-sm">No product image</p></div>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex-1 p-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {product?.category && <span className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-xs font-semibold border border-teal-200">{product.category}</span>}
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${isLevel2 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                Level {scanData.level} {isLevel2 ? "Verified" : "Scanned"}
              </span>
              {scanData.verified && <span className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold border border-emerald-200 flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Authentic</span>}
              <BlockchainBadge blockchain={(scanData as any).blockchain} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 leading-tight">{productName}</h1>
            {product?.brand && <p className="text-base text-gray-500 mb-3 font-medium">by {product.brand}</p>}
            {productDescription && <p className="text-gray-600 mb-5 leading-relaxed text-sm">{productDescription}</p>}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {isLevel2 && product?.pricing ? (
                <>
                  <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100"><div className="text-xl font-bold text-teal-600">${product.pricing.retail}</div><div className="text-xs text-gray-500 mt-0.5">Retail</div></div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100"><div className="text-xl font-bold text-slate-600">${product.pricing.wholesale}</div><div className="text-xs text-gray-500 mt-0.5">Wholesale</div></div>
                  {product.inventory && <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100"><div className="text-xl font-bold text-amber-600">{product.inventory.currentStock}</div><div className="text-xs text-gray-500 mt-0.5">In Stock</div></div>}
                  <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100"><div className="text-xl font-bold text-slate-600">{scanData?.stats?.totalScans || 0}</div><div className="text-xs text-gray-500 mt-0.5">Scans</div></div>
                </>
              ) : (
                <>
                  <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100"><div className="text-xl font-bold text-teal-600">{scanData.verified ? "✓" : "–"}</div><div className="text-xs text-gray-500 mt-0.5">Verified</div></div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100"><div className="text-xl font-bold text-slate-600">{scanData.stats?.totalScans || 0}</div><div className="text-xs text-gray-500 mt-0.5">Scans</div></div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100"><div className="text-xl font-bold text-slate-600">Lv {scanData.level}</div><div className="text-xs text-gray-500 mt-0.5">Access</div></div>
                </>
              )}
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-400">
              <span><strong className="text-gray-500">Tag:</strong> {scanData.tagId}</span>
              {product?.sku && <span><strong className="text-gray-500">SKU:</strong> {product.sku}</span>}
              {product?.batchId && <span><strong className="text-gray-500">Batch:</strong> {product.batchId}</span>}
              {product?.productId && <span><strong className="text-gray-500">Product:</strong> {product.productId}</span>}
            </div>

            {/* ⛓ Blockchain Proof Panel */}
            <BlockchainProofPanel blockchain={(scanData as any).blockchain} />

            {scanData.level === 1 && scanData.upgrade && (
              <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h4 className="font-semibold text-amber-900 mb-1 text-sm">Upgrade Available</h4>
                <p className="text-xs text-amber-700 mb-3">Get complete product traceability and rewards with Level 2 scanning.</p>
                {onUpgrade && <button onClick={onUpgrade} className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors text-xs font-semibold">Upgrade to Level 2</button>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const TabNav: React.FC = () => {
    const items = [
      { id: "overview", label: "Overview", icon: Eye },
      { id: "tagsmap", label: "Tags Map", icon: MapPin },
      { id: "tracking", label: "Supply Chain", icon: Truck },
      { id: "security", label: "Security", icon: Shield },
    ] as const;

    return (
      <div className="mb-8">
        <nav className="flex space-x-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
          {items.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id as any)}
              className={`py-2.5 px-4 rounded-lg font-medium text-sm flex items-center gap-2 transition-all whitespace-nowrap flex-1 justify-center ${activeTab === id ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <Icon className="w-4 h-4" /><span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </nav>
      </div>
    );
  };

  const Level1SimpleView: React.FC = () => {
    if (!scanData) return null;
    const isVerified = scanData.verified;
    const product = scanData.product;
    const productName = product?.name || scanData.basicInfo?.productName || `Tag ${scanData.tagId}`;
    const productBrand = product?.brand || scanData.business?.companyName || "";
    const productCategory = product?.category || "";
    const thumbUrl = pickProductImage(product);

    return (
      <div className="w-full max-w-md mx-auto space-y-4">
        {/* Shield */}
        <div className={`relative overflow-hidden rounded-3xl p-10 flex flex-col items-center ${isVerified ? "bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50" : "bg-gradient-to-br from-red-50 via-rose-50 to-orange-50"}`}>
          <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full border-[3px] ${isVerified ? "border-emerald-500/10" : "border-red-500/10"}`} />
          <div className={`absolute -bottom-8 -left-8 w-32 h-32 rounded-full border-[3px] ${isVerified ? "border-emerald-500/[0.06]" : "border-red-500/[0.06]"}`} />
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-5 ${isVerified ? "bg-emerald-500" : "bg-red-500"}`} style={{ boxShadow: isVerified ? "0 0 0 12px rgba(16,185,129,0.15), 0 0 0 24px rgba(16,185,129,0.07)" : "0 0 0 12px rgba(239,68,68,0.15), 0 0 0 24px rgba(239,68,68,0.07)" }}>
            {isVerified ? <CheckCircle className="w-12 h-12 text-white" strokeWidth={2.5} /> : <AlertTriangle className="w-12 h-12 text-white" strokeWidth={2.5} />}
          </div>
          <h1 className={`text-2xl font-bold text-center ${isVerified ? "text-emerald-900" : "text-red-900"}`}>{isVerified ? "Authentic Product" : "Verification Failed"}</h1>
          <p className={`text-sm text-center mt-1 ${isVerified ? "text-emerald-700/70" : "text-red-700/70"}`}>{isVerified ? "This product has been verified by JepTags" : "This product could not be verified"}</p>
        </div>

        {/* Product card */}
        {(productName || productBrand) && (
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              {thumbUrl ? <img src={thumbUrl} alt="" className="w-16 h-16 rounded-xl object-cover border-2 border-gray-100 flex-shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} /> : <div className="w-16 h-16 rounded-xl bg-gray-100 border-2 border-gray-100 flex items-center justify-center flex-shrink-0"><Package className="w-7 h-7 text-gray-300" /></div>}
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 text-base truncate">{productName}</h3>
                {productBrand && <p className="text-sm text-gray-500 truncate">by {productBrand}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Quick Facts */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "TAG ID", value: scanData.tagId || "N/A" },
              { label: "STATUS", value: isVerified ? "✓ Activated" : "✗ Not Found" },
              { label: "TOTAL SCANS", value: String(scanData.stats?.totalScans ?? 0) },
              { label: "CATEGORY", value: productCategory || "General" },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-xl px-3 py-2.5">
                <div className="text-[10px] text-gray-400 font-medium tracking-wider">{item.label}</div>
                <div className="text-sm font-semibold text-gray-900 mt-0.5 truncate">{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ⛓ Blockchain Proof Panel */}
        <BlockchainProofPanel blockchain={(scanData as any).blockchain} />

        {/* GS1 & Traceability */}
        {(scanData.product?.gs1?.gtin || scanData.business?.gs1?.gln) && (
          <div className="bg-white rounded-2xl border border-blue-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🏷️</span>
              <h4 className="font-semibold text-gray-800 text-sm">GS1 & Traceability</h4>
            </div>
            <div className="space-y-2 text-xs">
              {scanData.product?.gs1?.gtin && (
                <div className="flex justify-between items-center bg-blue-50 rounded-lg px-3 py-2">
                  <span className="text-gray-500">GTIN (Barcode)</span>
                  <span className="font-mono font-bold text-blue-700">{scanData.product.gs1.gtin}</span>
                </div>
              )}
              {scanData.product?.gs1?.companyPrefix && (
                <div className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-gray-500">GS1 Prefix</span>
                  <span className="font-mono font-medium text-gray-700">{scanData.product.gs1.companyPrefix}</span>
                </div>
              )}
              {scanData.business?.gs1?.gln && (
                <div className="flex justify-between items-center bg-green-50 rounded-lg px-3 py-2">
                  <span className="text-gray-500">Business GLN</span>
                  <span className="font-mono text-xs text-green-700 truncate ml-2 max-w-[180px]">{scanData.business.gs1.gln}</span>
                </div>
              )}
              {(scanData.traceability as any)?.epcis?.tagEpc && (
                <div className="flex justify-between items-center bg-purple-50 rounded-lg px-3 py-2">
                  <span className="text-gray-500">Tag EPC</span>
                  <span className="font-mono text-xs text-purple-700 truncate ml-2 max-w-[180px]">{(scanData.traceability as any).epcis.tagEpc}</span>
                </div>
              )}
              <div className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-gray-500">GS1 Status</span>
                <span className={`font-medium ${scanData.business?.gs1?.isOwnPrefix ? 'text-green-600' : 'text-blue-600'}`}>
                  {scanData.business?.gs1?.isOwnPrefix ? '✅ Own GS1 Prefix' : '🔵 JepTags Platform'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade CTA */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white">
          <div className="absolute -top-5 -right-5 w-20 h-20 rounded-full bg-white/5" />
          <div className="flex items-center gap-2 mb-3"><Shield className="w-5 h-5 text-amber-400" /><span className="text-xs font-bold text-amber-400 uppercase tracking-widest">Level 2 Available</span></div>
          <h3 className="text-lg font-bold mb-2">Unlock Full Details</h3>
          <p className="text-sm text-slate-400 leading-relaxed mb-4">Supply chain journey · Quality certificates · Store locations · Loyalty rewards</p>
          <button onClick={onUpgrade} className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/25">
            <Zap className="w-4 h-4" /> Verify & Unlock Level 2
          </button>
        </div>

        <p className="text-center text-[10px] text-gray-300 pb-2">Powered by JepTags Authenticity Platform</p>
      </div>
    );
  };

  // ═══════════════════════════════════════
  //  MAIN RETURN
  // ═══════════════════════════════════════

  if (!scanData) {
    return (
      <div className="w-full max-w-6xl mx-auto p-8 text-center">
        <div className="text-gray-500"><Package className="w-16 h-16 mx-auto mb-4" /><h3 className="text-xl font-semibold mb-2">No Scan Data Available</h3><p>Please scan a tag to view product information.</p></div>
      </div>
    );
  }

  // ── LEVEL 1: Simple single-page view (NO tabs) ──
  if (scanData.level !== 2) {
    return (
      <div className="w-full max-w-6xl mx-auto py-4 px-2">
        <Level1SimpleView />
      </div>
    );
  }

  // ── LEVEL 2: Full tabbed interface ──
  return (
    <div className="w-full max-w-6xl mx-auto">
      <ProductHero />
      <AuthenticityBadge />
      <TabNav />

      {activeTab === "overview" && (
        <OverviewTab
          scanData={scanData}
          store={store}
          stores={stores}
          loadingStores={isLoadingStores}
          batchDetails={batchDetails}
        />
      )}

      {activeTab === "tagsmap" && (
        <TagsMapTab
          scanData={scanData}
          mapLocations={mapLocations}
          loadingMap={isLoadingMapData}
        />
      )}

      {activeTab === "tracking" && (
        <TrackingTab
          scanData={scanData}
          shipments={shipments}
          loadingShipments={isLoadingShipments}
        />
      )}

      {activeTab === "security" && (
        <SecurityTab
          scanData={scanData}
          batchCertificates={batchCertificates}
          batchInspections={batchInspections}
          loadingQuality={loadingQuality}
          onSelectFile={setSelectedFile}
        />
      )}

      {(onShare || onUpgrade) && (
        <div className="mt-8 flex justify-center gap-4">
          {onShare && (
            <button onClick={onShare} className="bg-teal-600 text-white px-6 py-3 rounded-xl hover:bg-teal-700 transition-colors flex items-center gap-2 font-medium shadow-sm">
              <Share2 className="w-4 h-4" /> Share Result
            </button>
          )}

        </div>
      )}
    </div>
  );
};

export default LinkedInfoBlock;